import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Button,
  FormControl,
  Select,
  MenuItem,
  Typography,
  Divider,
  TextField,
  Tooltip,
  CircularProgress,
  Grid,
  Avatar,
} from "@mui/material";

import ShareLink from "./ShareLink";

import LoadingBackdrop from "./LoadingBackdrop";

import { AIModelSelect } from "./TestComponents2";
import { AIModelInfoTooltip } from "./AIModelInfoDialog";
import ShareDialog from "./ShareDialog";
import ShareIcon from "@mui/icons-material/Share";
import QuestionMarkIcon from "@mui/icons-material/Help";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import PrintAsPdf from "./PrintAsPdf";
import Translate from "./Translate";
import { BackButton } from "./functions";
import MoreToCome from "./MoreToCome";
import WelcomeDialogLessons from "./WelcomeDialogLessons";
import { AnimatedStep } from "./Animations";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import LoadingDialog from "./LoadingDialog";
import EditIcon from "@mui/icons-material/Edit";
import { IconButton } from "@mui/material";
import { useSearchParams, useParams } from "react-router-dom";
import ClueGame from "./ClueGame";
import GenerateIcon from "@mui/icons-material/AutoAwesome";
import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import InfoBox from "./InfoBox";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import { callApi } from "./api";
import TrainIcon from "@mui/icons-material/Train";
import { useSnackbar } from "./SnackbarHandler";
import LanguageSelector from "./LanguageSelector";

import { ThemeSelectorFlow } from "./ThemeSelectorFlow";
import HoverableImage from "./HoverableImage";
import ProgressBar from "./ProgressBar";
import ImageGenerationModal from "./ImageGenerationModal";

import { handlePrintPaSparet } from "./printFunctions";

import { FourSquare } from "react-loading-indicators";
import SaveAsIcon from "@mui/icons-material/SaveAs";
import { ThemeSelector } from "./ThemeSelector";
import Logger from "./Logger";

import ImageIcon from "@mui/icons-material/Image";
import { generateAndProcessImage, trackLesson } from "./functions";
import PrintIcon from "@mui/icons-material/Print";
import LessonDrawer from "./LessonDrawer";
import SaveLessonDialog from "./SaveLessonDialog";

import HistoryIcon from "@mui/icons-material/History";

import DeleteIcon from "@mui/icons-material/DeleteOutline";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import {
  Science as ScienceIcon,
  Castle as CastleIcon,
  Psychology as PsychologyIcon,
  Pets as PetsIcon,
  Public as PublicIcon,
  Brush as BrushIcon,
  EmojiEvents as EmojiEventsIcon,
  Museum as MuseumIcon,
  Nature as NatureIcon,
  AccountBalance as AccountBalanceIcon,
  LocalHospital as LocalHospitalIcon,
  School as SchoolIcon,
  Architecture as ArchitectureIcon,
} from "@mui/icons-material";

import { db } from "./firebase";
import {
  collection,
  updateDoc,
  query,
  where,
  limit,
  serverTimestamp,
  addDoc,
  getDocs,
} from "firebase/firestore";

const ClueGenerator = ({ user, theme, styles, grade, setMinimized }) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);

  const [topic, setTopic] = useState("customTheme");
  const bookContentRef = useRef("");
  const [customTheme, setCustomTheme] = useState("");
  const [language, setLanguage] = useState("svenska");
  const [clueCount, setClueCount] = useState(6);
  const [isGenerating, setIsGenerating] = useState(false);

  const [isSharing, setIsSharing] = useState(false);
  const [clueData, setClueData] = useState(null);
  const [isGeneratingTopicImage, setIsGeneratingTopicImage] = useState(false);
  const [isGeneratingClueImages, setIsGeneratingClueImages] = useState(false);
  const [progress, setProgress] = useState(0);
  const [generatedClueImages, setGeneratedClueImages] = useState(new Set());
  const { showSnackbar } = useSnackbar();

  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareDialogOpenFriends, setShareDialogOpenFriends] = useState(false);

  const [showImageModal, setShowImageModal] = useState(false);
  const [imageInstructions, setImageInstructions] = useState("");
  const [imageGenerationType, setImageGenerationType] = useState(null);
  const [openAiInstructions, setOpenAiInstructions] = useState("");

  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [titleText, setTitleText] = useState("På spåret");

  const [editingClueId, setEditingClueId] = useState(null);
  const [selectedClueIndex, setSelectedClueIndex] = useState(null);
  const inputRef = useRef(null);
  const [loadingImageIndex, setLoadingImageIndex] = useState(null);
  const [editingDifficulty, setEditingDifficulty] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const viewMode = searchParams.get("v") === "t";
  const [lessonTheme, setLessonTheme] = useState("");
  const [predefinedTheme, setPredefinedTheme] = useState(null);
  const lessonTitleRef = useRef(null); // Add this ref
  const transcriptRef = useRef("");
  const uploadedFileRef = useRef("");
  const urlContentRef = useRef("");
  const wikipediaContentRef = useRef("");
  const randomizedContentRef = useRef("");
  const coursePlanContentRef = useRef("");

  const pdfButtonRef = useRef(null);
  const translateButtonRef = useRef(null);
  const shareLinkButtonRef = useRef(null);

  const topicSectionRef = useRef(null);
  const factsSectionRef = useRef(null);

  const additionalThemes = [
    {
      type: "subheader",
      label: "Teman som lämpar sig extra bra för denna lektionstyp:",
    },
    {
      value: "virginia-woolf",
      label: "Virginia Woolf",
      content: "Virginia Woolf",
      Icon: MenuBookIcon,
    },
    {
      value: "eiffel-tower",
      label: "Eiffeltornet",
      content: "Eiffeltornet",
      Icon: CastleIcon,
    },
    {
      value: "penicillin",
      label: "Penicillin",
      content: "Penicillin",
      Icon: ScienceIcon,
    },
    {
      value: "allah",
      label: "Allah",
      content: "Allah",
      Icon: PublicIcon,
    },
    {
      value: "jesus",
      label: "Jesus",
      content: "Jesus",
      Icon: PublicIcon,
    },
    {
      value: "james-joyce",
      label: "James Joyce",
      content: "James Joyce",
      Icon: MenuBookIcon,
    },
    {
      value: "jane-austen",
      label: "Jane Austen",
      content: "Jane Austen",
      Icon: MenuBookIcon,
    },
    {
      value: "anna-karenina",
      label: "Anna Karenina",
      content: "Anna Karenina",
      Icon: MenuBookIcon,
    },
    {
      value: "mogadishu",
      label: "Mogadishu",
      content: "Mogadishu",
      Icon: PublicIcon,
    },
  ];

  const subject = searchParams.get("subject") || null;

  useEffect(() => {
    Logger.log("lessonTheme: ", lessonTheme);
  }, [lessonTheme]);

  useEffect(() => {
    Logger.log("predefinedTheme: ", predefinedTheme);
  }, [predefinedTheme]);

  useEffect(() => {
    Logger.log("customTheme: ", customTheme);
  }, [customTheme]);

  useEffect(() => {
    Logger.log("youtubeTranscript: ", transcriptRef.current);
  }, [transcriptRef.current]);

  useEffect(() => {
    Logger.log("wikipediaContentRef: ", wikipediaContentRef.current);
  }, [wikipediaContentRef.current]);
  useEffect(() => {
    Logger.log("uploadedFileRef: ", uploadedFileRef.current);
  }, [uploadedFileRef.current]);

  useEffect(() => {
    Logger.log("randomizedContentRef: ", randomizedContentRef.current);
  }, [randomizedContentRef.current]);

  useEffect(() => {
    Logger.log("coursePlanContentRef: ", coursePlanContentRef.current);
  }, [coursePlanContentRef.current]);

  const pasteTextContentRef = useRef("");
  const analysisContentRef = useRef("");
  const resetThemeContent = () => {
    setLessonTheme("");
    setCustomTheme("");
    setPredefinedTheme(null);
    transcriptRef.current = "";
    uploadedFileRef.current = "";
    wikipediaContentRef.current = "";
    randomizedContentRef.current = "";
    bookContentRef.current = "";
    urlContentRef.current = "";
    analysisContentRef.current = "";
    coursePlanContentRef.current = "";
    pasteTextContentRef.current = "";
  };

  const [currentStep, setCurrentStep] = useState(1);

  useEffect(() => {
    if (clueData) {
      setCurrentStep(3);
      setMinimized(true);
    } else {
      setMinimized(false);
    }
  }, [clueData, currentStep]);

  const [selectedModel, setSelectedModel] = useState("Studera.AI");
  const { lessonId } = useParams();

  const toggleViewMode = () => {
    const currentParams = Object.fromEntries(searchParams.entries());
    if (currentParams.v === "t") {
      delete currentParams.v;
    } else {
      currentParams.v = "t";
    }
    setSearchParams(currentParams);
  };

  useEffect(() => {
    const loadLessonFromId = async () => {
      if (lessonId) {
        try {
          const q = query(
            collection(db, "lesson_usage"),
            where("__name__", ">=", lessonId),
            where("__name__", "<=", lessonId + "\uf8ff"),
            where("lessonType", "==", "ClueGeneratorLesson.js"),
            limit(1),
          );

          const querySnapshot = await getDocs(q);
          if (!querySnapshot.empty) {
            const docData = querySnapshot.docs[0].data();

            if (!docData.hasOpened) {
              await updateDoc(querySnapshot.docs[0].ref, {
                hasOpened: true,
                openedAt: Date.now(),
              });
            }

            setClueData(docData.data);

            showSnackbar("Lektionen har laddats in.", "success");
          } else {
            showSnackbar("Kunde inte hitta lektionen", "error");
          }
        } catch (error) {
          console.error("Error loading lesson:", error);
          showSnackbar(
            "Ett fel uppstod när presentationen skulle laddas",
            "error",
          );
        }
      }
    };

    loadLessonFromId();
  }, lessonId);

  const handleDeleteClue = (clueKey) => {
    setClueData((prevData) => {
      const updatedClues = { ...prevData.clues };
      delete updatedClues[clueKey];

      return {
        ...prevData,
        clues: updatedClues,
      };
    });

    showSnackbar("Ledtråden har tagits bort", "success");
  };

  const handleEditClue = (index, newText, newDifficulty) => {
    const clueKey = `Clue ${index + 1}`;
    setClueData((prevData) => ({
      ...prevData,
      clues: {
        ...prevData.clues,
        [clueKey]: {
          ...prevData.clues[clueKey],
          text: newText,
          difficulty: newDifficulty,
        },
      },
    }));
    setEditingClueId(null);
    showSnackbar("Ledtråden har uppdaterats", "success");
  };

  const handleSaveLessonClick = () => {
    setSaveDialogOpen(true);
  };

  const handleSaveWithComment = async (comment, name, coverImage, tags) => {
    Logger.log(JSON.stringify(clueData, null, 2));
    await trackLesson(
      user.uid,
      "ClueGeneratorLesson.js",
      clueData,
      null,
      true,
      comment,
      name,
      coverImage,
      tags,
    );
    setDrawerOpen(true);
    showSnackbar("Lektionen sparad i din lektionsbank!", "success");
  };

  const handleShare = async (
    lessonComment,
    lessonName,
    selectedImage,
    tags,
    shareData,
  ) => {
    setIsSharing(true);
    await new Promise((resolve) => setTimeout(resolve, 1500));

    try {
      // Create a document for each student and then create notifications
      const sharePromises = shareData.studentIds.map(async (studentId) => {
        const documentData = {
          userId: user.uid,
          shared: true,
          sharedTo: studentId,
          comment: shareData.comment,
          sharedAt: Date.now(),
          fileUrl: shareData.fileUrl,
          lessonType: "ClueGeneratorLesson.js",
          timestamp: serverTimestamp(),
          lessonComment,
          lessonName,
          coverImage: selectedImage,
          hasPrinted: true,
          data: clueData,
        };
        // Create lesson document and get ID
        const lessonDoc = await addDoc(
          collection(db, "lesson_usage"),
          documentData,
        );
        Logger.log(
          "Adding document for student",
          studentId,
          ":",
          JSON.stringify(documentData, null, 2),
        );
        // Create notification with lesson doc ID
        await addDoc(collection(db, "notifications"), {
          type: "lesson_shared",
          recipientId: studentId,
          senderId: user.uid,
          read: false,
          createdAt: serverTimestamp(),
          lessonData: {
            lessonId: lessonDoc.id,
            lessonType: documentData.lessonType,
            lessonName: documentData.lessonName,
            fileUrl: documentData.fileUrl,
            comment: documentData.comment,
            coverImage: selectedImage,
          },
        });
        return lessonDoc;
      });
      await Promise.all(sharePromises);
      showSnackbar(`Du har nu delat lektionen!`, "success");
    } catch (error) {
      showSnackbar("Fel vid delande av lektionen: " + error.message, "error");
      console.error(error);
    } finally {
      setIsSharing(false);
    }
  };

  const handleGenerateSpecificImage = async (index) => {
    setIsGeneratingClueImages(true);
    setLoadingImageIndex(index);
    handleCloseModal();

    try {
      const clueKey = `Clue ${index + 1}`;
      const clue = clueData.clues[clueKey];
      const prompt = `Create a clue image for the hint: "${clue.text}" about ${clueData.topic}. Make it subtle and not too obvious. ${imageInstructions}`;
      const imageUrl = await generateAndProcessImage(prompt, user.uid);

      setClueData((prevData) => ({
        ...prevData,
        clues: {
          ...prevData.clues,
          [clueKey]: {
            ...prevData.clues[clueKey],
            image: imageUrl,
          },
        },
      }));

      showSnackbar("Bilden har genererats!", "success");
    } catch (error) {
      console.error("Error generating image:", error);
      showSnackbar("Ett fel uppstod när bilden skulle genereras", "error");
    } finally {
      setIsGeneratingClueImages(false);
      setLoadingImageIndex(null);
    }
  };

  const handleImageButtonClick = (type) => {
    setImageGenerationType(type);
    setSelectedClueIndex(null);
    setShowImageModal(true);
  };

  const handleGenerateWithInstructions = async () => {
    handleCloseModal();
    if (selectedClueIndex !== null) {
      await handleGenerateSpecificImage(selectedClueIndex);
      setSelectedClueIndex(null);
    } else if (imageGenerationType === "topic") {
      await handleGenerateTopicImage();
    } else if (imageGenerationType === "clues") {
      await handleGenerateClueImages();
    }
  };

  const handleCloseModal = () => {
    setShowImageModal(false);
    setImageInstructions("");
  };

  const simulateProgress = (
    startProgress,
    endProgress,
    duration,
    setProgressFn,
  ) => {
    const stepSize = 0.5;
    const steps = (endProgress - startProgress) / stepSize;
    const stepDuration = duration / steps;
    let currentProgress = startProgress;

    const intervalId = setInterval(() => {
      currentProgress += stepSize;
      if (currentProgress >= endProgress) {
        clearInterval(intervalId);
      } else {
        setProgressFn(currentProgress);
      }
    }, stepDuration);

    return () => clearInterval(intervalId);
  };

  const handleGenerateClues = async () => {
    setIsGenerating(true);
    setProgress(0);
    let stopProgressSimulation = null;
    try {
      stopProgressSimulation = simulateProgress(0, 90, 15000, setProgress);

      let themeToUse = customTheme;

      // if (lessonTheme === "youtube") {
      //   themeToUse = transcriptRef.current;
      // } else if (lessonTheme === "customTheme") {
      //   themeToUse = customTheme;
      // } else if (lessonTheme === "predefined") {
      //   themeToUse = predefinedTheme;
      // } else if (lessonTheme === "fileUpload") {
      //   themeToUse = uploadedFileRef.current;
      // } else if (lessonTheme === "wikipedia") {
      //   themeToUse = wikipediaContentRef.current;
      // } else if (lessonTheme === "randomizer") {
      //   themeToUse = randomizedContentRef.current;
      // } else if (lessonTheme === "book") {
      //   themeToUse = bookContentRef.current;
      // } else if (lessonTheme === "url") {
      //   themeToUse = urlContentRef.current;
      // } else if (lessonTheme === "vision") {
      //   themeToUse = analysisContentRef.current;
      // } else if (lessonTheme === "pasteText") {
      //   themeToUse = pasteTextContentRef.current;
      // } else {
      //   //
      //   themeToUse = coursePlanContentRef.current;
      // }

      Logger.log("themeToUse: ", themeToUse);
      //const themeToUse = topic === 'customTheme' ? customTheme : topic;

      const response = await callApi(
        [
          {
            role: "system",
            content: `You are an assistant that generates interesting clues for guessing games. The clues should start VERY VERY difficult and become progressively easier. ${openAiInstructions}`,
          },
          {
            role: "user",
            content: `Generate a JSON object with exactly ${clueCount} clues about ${themeToUse} in "${language}". 


            The clues should be ordered from most difficult to easiest, with the last clue being very obvious.
            Each clue should contain at least 3 sentences and max 3 sentences.
            Include empty image placeholders (null) for "topicImage" and each clue's "image" field.
            Example response:
{
  "topic": "Miguel de Cervantes",

  "answer": "Miguel de Cervantes",
    "title": "Famous Authors in History",
  "topicImage": null,
  "clues": {
    "Clue 1": {
      "text": "This author spent five years as a captive in Algiers",
      "difficulty": "12 poäng",
      "image": null
    },
    "Clue 2": {
      "text": "They worked as a tax collector before achieving literary fame",
      "difficulty": "10 poäng",
      "image": null
    },
    "Clue 3": {
      "text": "They lost the use of their left hand in a famous naval battle",
      "difficulty": "8 poäng",
      "image": null
    },
    "Clue 4": {
      "text": "This Spanish author's masterpiece features a delusional knight",
      "difficulty": "6 poäng",
      "image": null
    },
    "Clue 5": {
      "text": "They wrote Don Quixote",
      "difficulty": "4 poäng",
      "image": null
    }

        "Clue 6": {
      "text": "They wrote Don Quixote",
      "difficulty": "2 poäng",
      "image": null
    }
  }
}`,
          },
        ],
        8000,
        true,
        user.uid,
      );

      if (stopProgressSimulation) {
        stopProgressSimulation();
      }

      setProgress(95);

      let messageContent = response.data.choices[0].message.content.trim();
      if (messageContent.startsWith("```json")) {
        messageContent = messageContent.slice(7, -3).trim();
      } else if (messageContent.startsWith("```")) {
        messageContent = messageContent.slice(3, -3).trim();
      }

      const data = JSON.parse(messageContent);
      setProgress(100);
      await new Promise((resolve) => setTimeout(resolve, 800));
      Logger.log(JSON.stringify(data, null, 2));
      setClueData(data);
      setTitleText(data.answer);
      showSnackbar("På spåret-lektionen har genererats!", "success");

      await trackLesson(user.uid, "ClueGenerator.js", null, {
        language: language,
        clueCount: clueCount,
        themeToUse: themeToUse,
        lessonTheme: lessonTheme,
      });
    } catch (error) {
      console.error("Error generating clues:", error);
      showSnackbar("Något gick fel. Försök igen.", "error");
    } finally {
      setIsGenerating(false);
    }
  };

  const handleGenerateTopicImage = async () => {
    setIsGeneratingTopicImage(true);
    try {
      const prompt = `Skapa en bild som representerar ${clueData.topic}, men gör det inte för uppenbart vad det är. Lägg inte till någon text i bilden. ${imageInstructions}`;
      const imageUrl = await generateAndProcessImage(prompt, user.uid);
      setClueData((prevData) => ({
        ...prevData,
        topicImage: imageUrl,
      }));
      showSnackbar("Ämnesbild genererad framgångsrikt.", "success");
    } catch (error) {
      showSnackbar(
        "Misslyckades med att generera ämnesbild. Försök igen.",
        "error",
      );
    } finally {
      setIsGeneratingTopicImage(false);
    }
  };

  const handleGenerateClueImages = async () => {
    setIsGeneratingClueImages(true);
    setGeneratedClueImages(new Set());
    handleCloseModal();

    try {
      const entries = Object.entries(clueData.clues);
      await Promise.all(
        entries.map(async ([key, value]) => {
          try {
            const prompt = `Skapa en bildledtråd för ledtråden: "${value.text}" om ${clueData.topic}. Gör den subtil och inte för uppenbar. Bilden får absolut INTE avslöja svaret. Lägg inte till någon text. ${imageInstructions}`;
            const imageUrl = await generateAndProcessImage(prompt, user.uid);

            setClueData((prevData) => ({
              ...prevData,
              clues: {
                ...prevData.clues,
                [key]: {
                  ...prevData.clues[key],
                  image: imageUrl,
                },
              },
            }));

            setGeneratedClueImages((prev) => new Set([...prev, key]));
            showSnackbar(
              `Bild för ledtråd ${key.replace("Clue ", "")} har genererats!`,
              "success",
            );
          } catch (error) {
            console.error(`Error generating image for clue "${key}":`, error);
          }
        }),
      );

      showSnackbar("Alla bilder har genererats!", "success");
    } catch (error) {
      showSnackbar("Ett fel uppstod när bilderna skulle genereras", "error");
    } finally {
      setIsGeneratingClueImages(false);
    }
  };

  const isGeneratingAnyImages =
    isGeneratingTopicImage || isGeneratingClueImages;

  return (
    <Box sx={styles.container}>
      <Box
        sx={{
          ...styles.innerContainer,
          width: { xs: "100%", md: "100%" },
          margin: "0 auto",
        }}
      >
        {!viewMode && (
          <>
            {currentStep !== 3 && (
              <Typography
                variant="h1"
                gutterBottom
                sx={{
                  ...styles.newH1,
                }}
              >
                På spåret
              </Typography>
            )}
          </>
        )}
        <Box
          sx={{
            ...styles.buttonsContainer,
            ...styles.additionalButtonsContainer,
          }}
        >
          {currentStep === 3 && (
            <Tooltip
              title={
                viewMode
                  ? "Redigeringsläget"
                  : "Hur lektionen ser ut för eleverna "
              }
            >
              <IconButton
                onClick={() => toggleViewMode()}
                sx={{ ...styles.iconButtonBg }}
              >
                {viewMode ? <EditIcon /> : <PersonOutlineIcon />}
              </IconButton>
            </Tooltip>
          )}
          {!viewMode && (
            <>
              <Tooltip title="Dina sparade lektioner">
                <IconButton
                  onClick={() => setDrawerOpen(true)}
                  sx={{ ...styles.iconButtonBg }}
                >
                  <BookmarkBorderIcon />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Box>

        <LessonDrawer
          userId={user.uid}
          lessonType="ClueGeneratorLesson.js"
          theme={theme}
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          lessonIcon={TrainIcon}
          onSelectLesson={(lesson) => {
            setClueData(lesson.data);
            setDrawerOpen(false);
            //setShowForm(false);
            showSnackbar(`Lektionen har laddats in.`, "success");
            setTimeout(() => {
              lessonTitleRef.current?.scrollIntoView({
                behavior: "smooth",
                block: "start", // This ensures it scrolls to the top of the element
              });
            }, 100);
          }}
        />
        {!viewMode && (
          <>
            {currentStep !== 3 && (
              <InfoBox
                icon={TrainIcon}
                title="Instruktioner för Ledtråds Generator"
                instructions={[
                  "Ange ett svar som elever ska gissa sig till (t.ex. Penicillin, Kalla kriget)",
                  "Välj antal ledtrådar du vill att eleverna ska få",
                  "Lägg till bilder för varje ledtråd och skriv ut lektionen",
                ]}
                theme={theme}
                styles={styles}
                user={user}
                lessonType="ClueGeneratorLesson.js"
              />
            )}
            {currentStep === 1 && (
              <>
                <Box sx={{ ...styles.headlineLessonBox, mt: 8 }}>
                  <Typography variant="h6" sx={styles.headlineLessonBoxTitle}>
                    <span>1</span> Skriv önskat svar
                  </Typography>
                </Box>

                <Box
                  sx={{
                    ...styles.freshBox,
                    width: { xs: "100%", md: "70%" },
                    margin: "0 auto",
                    padding: "40px",
                  }}
                >
                  <TextField
                    fullWidth
                    value={customTheme}
                    onChange={(e) => setCustomTheme(e.target.value)}
                    placeholder="T.ex. Penicillin, Andra världskriget eller Cleopatra"
                    variant="outlined"
                    multiline
                    autoFocus
                    maxLength={200}
                    rows={1}
                    InputProps={{
                      style: {
                        fontWeight: "500",
                        fontSize: "1.4rem",
                        textAlign: "center",
                      },
                    }}
                    inputProps={{
                      style: {
                        textAlign: "center",
                      },
                    }}
                  />
                  <Box sx={{ textAlign: "center" }}>
                    <Button
                      variant="purp"
                      sx={{ mt: 4 }}
                      onClick={() => {
                        //const currentValue = inputRef.current?.value || "";

                        if (!customTheme.trim()) {
                          showSnackbar(
                            "Skriv in det önskade rätta svaret",
                            "info",
                          );
                          return;
                        }
                        setCurrentStep(2);
                      }}
                      endIcon={<ArrowForwardIosIcon />}
                    >
                      Nästa
                    </Button>
                  </Box>
                </Box>
                <Box sx={{ display: "none" }}>
                  <ThemeSelectorFlow
                    value={lessonTheme}
                    subject={subject}
                    onChange={(e) => setLessonTheme(e.target.value)}
                    customTheme={customTheme}
                    onCustomThemeChange={(e) => {
                      Logger.log("onCustomThemeChange", e.target.value);
                      setCustomTheme(e.target.value);
                      setCurrentStep(2);
                    }}
                    onPastedTextChange={(content) => {
                      Logger.log("onPastedTextChange", content.target.value);
                      pasteTextContentRef.current = content.target.value;
                      setCurrentStep(2);
                    }}
                    onAnalysisContentChange={(content) => {
                      Logger.log("onAnalysisContentChange", content.analysis);
                      analysisContentRef.current = content.analysis;
                      setCurrentStep(2);
                    }}
                    onBookContentChange={(content) => {
                      Logger.log(
                        "onBookContentChange",
                        content.substring(0, 100),
                      );
                      bookContentRef.current = content;
                      setCurrentStep(2);
                    }}
                    onUrlContentChange={(content) => {
                      Logger.log(
                        "Parent: URL content received",
                        content.substring(0, 300),
                      );
                      urlContentRef.current = content;
                      setCurrentStep(2);
                    }}
                    onPredefinedThemeChange={(e) =>
                      setPredefinedTheme(e.target.value)
                    }
                    onYoutubeTranscriptChange={(transcriptContent) => {
                      Logger.log(
                        "onYoutubeTranscriptChange körs med: ",
                        transcriptContent,
                      );
                      transcriptRef.current = transcriptContent;
                      setCurrentStep(2);
                    }}
                    onFileUpload={(content) => {
                      Logger.log("onFileUpload körs med: ", content);
                      uploadedFileRef.current = content;
                      setCurrentStep(2);
                    }}
                    onWikipediaContentChange={(content) => {
                      Logger.log(
                        "Parent: Wikipedia content received",
                        content.substring(0, 100),
                      );
                      wikipediaContentRef.current = content;
                      setCurrentStep(2);
                    }}
                    onRandomizedContentChange={(content) => {
                      Logger.log(
                        "randomizedContentRef",
                        content.substring(0, 100),
                      );
                      randomizedContentRef.current = content;
                      setCurrentStep(2);
                    }}
                    onCoursePlanContentChange={(content) => {
                      Logger.log(
                        "onCoursePlanChange",
                        content.substring(0, 100),
                      );
                      coursePlanContentRef.current = content;
                      setCurrentStep(2);
                    }}
                    lessonType="ClueGeneratorLesson.js"
                    user={user}
                    theme={theme}
                    styles={styles}
                    onLessonSelect={(lesson) => {
                      Logger.log("onLessonSelect körs");
                      setClueData(lesson.data);
                      setTimeout(() => {
                        lessonTitleRef.current?.scrollIntoView({
                          behavior: "smooth",
                          block: "start", // This ensures it scrolls to the top of the element
                        });
                      }, 100);
                      showSnackbar("Lektionen har laddats in.", "success");
                    }}
                    additionalThemes={additionalThemes}
                  />
                </Box>
              </>
            )}

            {currentStep === 2 && (
              <>
                <BackButton
                  currentStep={currentStep}
                  onClick={() => {
                    setCurrentStep(currentStep - 1);

                    resetThemeContent();
                  }}
                  show={currentStep !== 1}
                />
                <AnimatedStep isRevealed={true}>
                  <Box sx={{ ...styles.headlineLessonBox, mt: 6 }}>
                    <Typography variant="h6" sx={styles.headlineLessonBoxTitle}>
                      <span>2</span> Generera lektionen
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      ...styles.freshBox,
                      padding: "30px",
                      width: { xs: "100%", md: "70%" },
                      margin: "0 auto",
                    }}
                  >
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: "500", mt: 1 }}
                        >
                          Antal ledtrådar:
                        </Typography>
                        <FormControl
                          fullWidth
                          margin="normal"
                          sx={{ mb: 1, mt: 1 }}
                        >
                          <Select
                            value={clueCount}
                            onChange={(e) => setClueCount(e.target.value)}
                          >
                            <MenuItem value={3}>3</MenuItem>
                            <MenuItem value={4}>4</MenuItem>
                            <MenuItem value={5}>5</MenuItem>
                            <MenuItem value={6}>6</MenuItem>
                            <MenuItem value={7}>7</MenuItem>
                            <MenuItem value={8}>8</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: "500", mt: 0.8 }}
                        >
                          Språk:
                        </Typography>
                        <Box sx={{ mt: 1, mb: 1 }}>
                          <LanguageSelector
                            language={language}
                            setLanguage={setLanguage}
                          />
                        </Box>
                      </Grid>

                      <Grid item xs={6} md={6}>
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: "500", mt: 1 }}
                        >
                          Ytterligare instruktioner:
                        </Typography>
                        <FormControl
                          fullWidth
                          margin="normal"
                          sx={{ mb: 3, mt: 1 }}
                        >
                          <TextField
                            value={openAiInstructions}
                            onChange={(e) =>
                              setOpenAiInstructions(e.target.value)
                            }
                            placeholder="Ex. Fokusera på vetenskapliga prestationer"
                            variant="outlined"
                          />
                        </FormControl>
                      </Grid>

                      <Grid item xs={6} md={6} sx={{ mt: 1 }}>
                        <Typography
                          variant="formLabelSinUnderline"
                          sx={{ fontWeight: "500", mt: 0.8 }}
                        >
                          AI-modell
                        </Typography>
                        <AIModelInfoTooltip>
                          <span style={{ cursor: "help" }}>
                            <QuestionMarkIcon
                              sx={{
                                fontSize: 16,
                                ml: 1,
                                verticalAlign: "middle",
                              }}
                            />
                          </span>
                        </AIModelInfoTooltip>

                        <AIModelSelect
                          selectedModel={selectedModel}
                          setSelectedModel={setSelectedModel}
                        />
                      </Grid>
                    </Grid>
                    <Box sx={{ textAlign: "center" }}>
                      <Button
                        variant={
                          theme.palette.mode === "light" ? "purp" : "contained"
                        }
                        color="primary"
                        onClick={handleGenerateClues}
                        disabled={isGenerating || isGeneratingAnyImages}
                        sx={{ mt: 2 }}
                        startIcon={
                          !isGenerating ? (
                            <GenerateIcon style={{ color: "#fff" }} />
                          ) : (
                            <CircularProgress
                              style={{ color: "#fff" }}
                              size={16}
                            />
                          )
                        }
                      >
                        {isGenerating
                          ? "Genererar ledtrådar..."
                          : "Generera lektionen"}
                      </Button>
                    </Box>
                    {isGenerating && (
                      <Box sx={{ mt: 7, display: "none" }}>
                        <ProgressBar
                          progress={progress}
                          label="Genererar på spåret-lektion..."
                        />
                      </Box>
                    )}
                  </Box>
                </AnimatedStep>
              </>
            )}
          </>
        )}
        {currentStep === 3 && (
          <>
            <BackButton
              currentStep={currentStep}
              onClick={() => {
                setCurrentStep(1);
                setClueData("");
                resetThemeContent();
              }}
              show={currentStep !== 1}
            />
            {clueData && (
              <>
                <Box
                  ref={lessonTitleRef}
                  sx={{ ...styles.headlineLessonBox, mt: viewMode ? 0 : 2 }}
                >
                  <Typography
                    variant="h6"
                    sx={{ ...styles.headlineLessonBoxTitle, display: "none" }}
                  >
                    <span>3</span> Din lektion
                  </Typography>
                </Box>

                {viewMode ? (
                  <ClueGame
                    clueData={clueData}
                    user={user}
                    titleText={titleText}
                    theme={theme}
                    styles={styles}
                  />
                ) : (
                  <Box
                    sx={{
                      display: "flex",
                      gap: 3,
                      flexDirection: {
                        xs: "column",
                        md: "row",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        ...styles.freshBox,
                        flex: "1 1 auto",
                        padding: "30px",

                        width: viewMode ? "90%" : "100%",
                        ...(viewMode
                          ? { flex: "0 0 70%" }
                          : { flex: "1 1 auto" }),
                        margin: "0 auto",
                        mt: viewMode ? 0 : 0,
                      }}
                    >
                      <SaveLessonDialog
                        key={clueData?.title || ""}
                        open={saveDialogOpen}
                        onClose={() => setSaveDialogOpen(false)}
                        onSave={handleSaveWithComment}
                        theme={theme}
                        lessonName={clueData?.answer || ""}
                        lessonObject={clueData}
                        lessonType="ClueGeneratorLesson.js"
                      />
                      <ImageGenerationModal
                        open={showImageModal}
                        onClose={handleCloseModal}
                        onGenerate={handleGenerateWithInstructions}
                        isGenerating={
                          imageGenerationType === "topic"
                            ? isGeneratingTopicImage
                            : isGeneratingClueImages
                        }
                        instructions={imageInstructions}
                        setInstructions={setImageInstructions}
                        generationType={imageGenerationType}
                        theme={theme}
                      />
                      <Box sx={{ mt: 0 }}>
                        {isEditingTitle ? (
                          <TextField
                            value={titleText}
                            onChange={(e) => setTitleText(e.target.value)}
                            onBlur={() => {
                              setIsEditingTitle(false);
                              showSnackbar(
                                "Titeln på lektionen har uppdaterats",
                                "success",
                              );
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                setIsEditingTitle(false);
                                showSnackbar(
                                  "Titeln på lektionen har uppdaterats",
                                  "success",
                                );
                              }
                            }}
                            autoFocus
                            fullWidth
                            variant="standard"
                            sx={{ backgroundColor: "transparent" }}
                            InputProps={{
                              style: {
                                fontWeight: "bold",
                                fontSize: "2.2em",
                                textAlign: "center",

                                backgroundColor: "transparent",
                              },
                              disableUnderline: true,
                            }}
                          />
                        ) : (
                          <Box
                            sx={{
                              position: "relative",
                              display: "inline-block",
                              margin: "0 auto",
                              mb: 3,
                              mt: 0,
                              ":hover .edit-icon": {
                                opacity: viewMode ? 0 : 1,
                              },
                            }}
                          >
                            <Typography
                              variant="h1"
                              sx={{
                                fontWeight: "600",
                                fontSize: "2.4em",
                                cursor: "pointer",
                              }}
                              onClick={() => setIsEditingTitle(true)}
                            >
                              {titleText}
                            </Typography>
                            <Tooltip title="Redigera titel">
                              <EditIcon
                                className="edit-icon"
                                onClick={() => setIsEditingTitle(true)}
                                sx={{
                                  position: "absolute",
                                  right: -35,
                                  top: "50%",
                                  transform: "translateY(-50%)",
                                  cursor: "pointer",
                                  opacity: 0,
                                  transition: "opacity 0.2s ease",
                                }}
                              />
                            </Tooltip>
                          </Box>
                        )}

                        <Grid container spacing={4}>
                          {Object.entries(clueData.clues)
                            .sort(([keyA, clueA], [keyB, clueB]) => {
                              // Extract the numeric part from the key (e.g., "Clue 1" -> 1)
                              const numA = parseInt(
                                keyA.replace(/\D/g, ""),
                                10,
                              );
                              const numB = parseInt(
                                keyB.replace(/\D/g, ""),
                                10,
                              );

                              // Alternatively, sort by difficulty points if that's more reliable
                              // const pointsA = parseInt(clueA.difficulty.match(/\d+/)[0], 10);
                              // const pointsB = parseInt(clueB.difficulty.match(/\d+/)[0], 10);
                              // return pointsB - pointsA;  // Descending order

                              return numA - numB; // Ascending order by clue number
                            })
                            .map(([key, clue], index) => (
                              <Grid item xs={12} key={index}>
                                <Box
                                  sx={{
                                    position: "relative",
                                    "&:hover .clue-controls": {
                                      opacity: 1,
                                    },
                                  }}
                                >
                                  <Box
                                    className="clue-controls"
                                    sx={{
                                      position: "absolute",
                                      right: "-10px",
                                      top: "20px",
                                      opacity: 0,
                                      transition: "opacity 0.2s",
                                      zIndex: 1,
                                      display: "flex",
                                      flexDirection: "row",
                                      gap: "4px",
                                    }}
                                  >
                                    {editingClueId === index ? (
                                      <Tooltip title="Spara" placement="top">
                                        <IconButton
                                          onClick={() => {
                                            const textElement =
                                              document.querySelector(
                                                `#clue-text-${index}`,
                                              );
                                            const difficultyElement =
                                              document.querySelector(
                                                `#difficulty-${index}`,
                                              );
                                            handleEditClue(
                                              index,
                                              textElement.value,
                                              difficultyElement.value,
                                            );
                                          }}
                                        >
                                          <SaveAsIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    ) : (
                                      <Tooltip title="Redigera" placement="top">
                                        <IconButton
                                          onClick={() =>
                                            setEditingClueId(index)
                                          }
                                        >
                                          <EditIcon fontSize="medium" />
                                        </IconButton>
                                      </Tooltip>
                                    )}
                                    <Tooltip title="Ta bort" placement="top">
                                      <IconButton
                                        onClick={() => handleDeleteClue(key)}
                                      >
                                        <DeleteIcon fontSize="medium" />
                                      </IconButton>
                                    </Tooltip>
                                  </Box>

                                  {editingClueId === index ? (
                                    <TextField
                                      id={`difficulty-${index}`}
                                      defaultValue={clue.difficulty}
                                      variant="outlined"
                                      size="small"
                                      sx={{
                                        mb: 1,
                                        mt: 5,

                                        "& .MuiInputBase-input": {
                                          fontSize: "40px",
                                          color: (theme) =>
                                            theme.palette.primary.main,
                                          fontWeight: "bold",
                                        },
                                      }}
                                    />
                                  ) : (
                                    <Typography
                                      variant="subtitle1"
                                      sx={{
                                        fontWeight: "bold",
                                        mb: 1,
                                        mt: 5,
                                        color: (theme) =>
                                          theme.palette.primary.main,
                                        fontSize: "40px",
                                      }}
                                    >
                                      {clue.difficulty}
                                    </Typography>
                                  )}

                                  <Box
                                    sx={{
                                      backgroundColor:
                                        theme.palette.mode === "dark"
                                          ? "#15202b"
                                          : "#e0e0e029",
                                      borderRadius: "10px",
                                      padding: "20px",
                                    }}
                                  >
                                    <Grid
                                      container
                                      spacing={3}
                                      alignItems="center"
                                    >
                                      <Grid item xs={12} md={4}>
                                        <HoverableImage
                                          imageUrl={clue.image}
                                          isLoading={isGeneratingClueImages}
                                          loadingIndex={loadingImageIndex}
                                          currentIndex={index}
                                          onGenerateClick={() => {
                                            setSelectedClueIndex(index);
                                            setShowImageModal(true);
                                          }}
                                          userId={user.uid}
                                          onImageUpdate={(newImageUrl) => {
                                            setClueData((prevData) => ({
                                              ...prevData,
                                              clues: {
                                                ...prevData.clues,
                                                [key]: {
                                                  ...prevData.clues[key],
                                                  image: newImageUrl,
                                                },
                                              },
                                            }));
                                          }}
                                          height="240px"
                                          theme={theme}
                                          showSnackbar={showSnackbar}
                                        />
                                      </Grid>
                                      <Grid
                                        item
                                        xs={12}
                                        md={8}
                                        sx={{
                                          display: "flex",
                                          flexDirection: "column",
                                        }}
                                      >
                                        {editingClueId === index ? (
                                          <TextField
                                            id={`clue-text-${index}`}
                                            fullWidth
                                            multiline
                                            defaultValue={clue.text}
                                            autoFocus
                                            sx={{
                                              ...styles.editingTextField,
                                              "& .MuiInputBase-input": {
                                                lineHeight: "1.7rem",
                                              },
                                            }}
                                          />
                                        ) : (
                                          <Typography
                                            variant="body1"
                                            sx={{
                                              lineHeight: "1.7rem",
                                              whiteSpace: "pre-wrap",
                                            }}
                                          >
                                            {clue.text}
                                          </Typography>
                                        )}
                                      </Grid>
                                    </Grid>
                                  </Box>
                                </Box>
                              </Grid>
                            ))}
                        </Grid>
                        <Typography
                          variant="h6"
                          sx={{
                            ...styles.grayText,
                            fontWeight: "600",
                            mt: 8,
                            textAlign: "center",
                            fontSize: "1.8",
                            mb: 5,
                          }}
                        >
                          Korrekt svar: {clueData.answer}
                        </Typography>
                      </Box>
                    </Box>

                    {!viewMode && (
                      <Box sx={{ position: "relative" }}>
                        <Box
                          sx={{
                            ...styles.lessonControlPanelNuevo2,

                            "& .MuiButton-root": {
                              ...styles.lessonControlPanelNewButtons2,
                            },
                          }}
                        >
                          <Typography
                            variant="subtitle1"
                            sx={{ fontWeight: 600, color: "text.secondary" }}
                          >
                            Hantera
                          </Typography>

                          <Translate
                            buttonRef={translateButtonRef}
                            lessonData={clueData}
                            onTranslated={setClueData}
                            callApi={callApi}
                            user={user}
                            showSnackbar={showSnackbar}
                          />

                          <Button
                            variant={
                              theme.palette.mode === "light"
                                ? "purp"
                                : "contained"
                            }
                            color="primary"
                            onClick={() => handleImageButtonClick("clues")}
                            disabled={isGeneratingAnyImages}
                            startIcon={
                              !isGeneratingClueImages ? (
                                <ImageIcon />
                              ) : (
                                <CircularProgress
                                  style={{ color: "#fff" }}
                                  size={16}
                                />
                              )
                            }
                          >
                            {isGeneratingClueImages
                              ? "Genererar..."
                              : "Generera bilderna"}
                          </Button>

                          <Typography
                            variant="subtitle1"
                            sx={{
                              fontWeight: 600,
                              color: "text.secondary",
                              mt: 3,
                            }}
                          >
                            Visa
                          </Typography>
                          <Tooltip title="Hur lektionen ser ut för eleverna">
                            <Button
                              variant="success"
                              onClick={() => {
                                window.scrollTo({ top: 0, behavior: "smooth" });
                                toggleViewMode();
                              }}
                              startIcon={
                                searchParams.get("v") === "t" ? (
                                  <PersonOutlineIcon />
                                ) : (
                                  <PersonOutlineIcon />
                                )
                              }
                            >
                              {searchParams.get("v") === "t"
                                ? "Redigeringsläge"
                                : "Elevläge"}
                            </Button>
                          </Tooltip>

                          <Typography
                            variant="subtitle1"
                            sx={{
                              fontWeight: 600,
                              color: "text.secondary",
                              mt: 3,
                            }}
                          >
                            Spara
                          </Typography>
                          <Button
                            variant="print"
                            startIcon={<SaveAsIcon />}
                            sx={{ ml: 2 }}
                            onClick={handleSaveLessonClick}
                          >
                            Spara i lektionsbanken
                          </Button>
                          <Button
                            variant="print"
                            color="primary"
                            onClick={() =>
                              handlePrintPaSparet(clueData, titleText, theme)
                            }
                            startIcon={<PrintIcon />}
                            sx={{ ml: 2 }}
                          >
                            Skriv ut
                          </Button>
                          <PrintAsPdf
                            buttonRef={pdfButtonRef}
                            onPrint={() =>
                              handlePrintPaSparet(clueData, titleText, theme)
                            }
                            sx={{ ml: 2 }}
                          />

                          <Typography
                            variant="subtitle1"
                            sx={{
                              fontWeight: 600,
                              color: "text.secondary",
                              mt: 3,
                            }}
                          >
                            Dela
                          </Typography>
                          <ShareLink
                            buttonRef={shareLinkButtonRef}
                            key={lessonId}
                            lessonId={lessonId}
                            lessonData={clueData}
                            user={user}
                            lessonType={"ClueGeneratorLesson.js"}
                            onCopy={(message, severity) =>
                              showSnackbar(message, severity)
                            }
                          />

                          <Button
                            variant="success"
                            sx={{}}
                            onClick={() => setShareDialogOpenFriends(true)}
                            startIcon={<ShareIcon />}
                          >
                            Dela med vänner
                          </Button>
                          <Button
                            variant="success"
                            sx={{
                              mt: 2,
                              ml: 2,
                            }}
                            onClick={() => setShareDialogOpen(true)}
                            startIcon={<ShareIcon />}
                          >
                            Dela med elever
                          </Button>
                          <Box>
                            <MoreToCome user={user} />
                          </Box>
                        </Box>
                      </Box>
                    )}
                  </Box>
                )}
              </>
            )}
          </>
        )}

        <ShareDialog
          open={shareDialogOpen}
          onClose={() => setShareDialogOpen(false)}
          onShare={handleShare}
          user={user}
          isLessonSharing={true}
          theme={theme}
          lessonName={clueData?.title || ""}
          lessonObject={clueData}
          lessonType={"ClueGeneratorLesson.js"}
        />

        <ShareDialog
          open={shareDialogOpenFriends}
          onClose={() => setShareDialogOpenFriends(false)}
          onShare={handleShare}
          user={user}
          isLessonSharing={true}
          theme={theme}
          lessonName={clueData?.title || ""}
          lessonObject={clueData}
          lessonType={"ClueGeneratorLesson.js"}
          shareWithFriends={true}
        />

        <LoadingBackdrop
          isOpen={isSharing}
          imagePath="/img/AI.png"
          loadingText={"Delar lektionen..."}
        />

        <LoadingDialog
          open={isGenerating}
          onClose={() => {}}
          progress={progress}
          progressLabel={"Genererar lektion — tar 30-60 sekunder..."}
          title="Genererar lektion. Snart klart..."
          snackbarMessage="Genereringen är snart klar. Vänligen vänta."
          snackbarSeverity="info"
          progressBarHeight="15px"
          progressBarFontSize="0.9rem"
          coffee={false}
        />

        <LoadingDialog
          open={isGeneratingClueImages}
          onClose={() => {}}
          progress={progress}
          progressLabel={"Genererar bilderna — tar 30-60 sekunder..."}
          hideProgressBar={true}
          title="Genererar bilderna. Snart klart..."
          snackbarMessage="Genereringen är snart klar. Vänligen vänta."
          snackbarSeverity="info"
          progressBarHeight="15px"
          progressBarFontSize="0.9rem"
          coffee={false}
        />

        <WelcomeDialogLessons
          key={currentStep}
          lessonType="ClueGeneratorLesson.js"
          open={currentStep === 3}
          title="Välkommen till på spåret!"
          imageSrc="https://sportkuriren.se/wp-content/uploads/train.png"
          imageSrcMaxWidth="50%"
          items={[
            {
              icon: "Edit",
              text: "Du kan redigera eller ta bort vissa ledtrådar vid behov",
              color: "#ff9e7d",
            },
            {
              icon: "Image",
              text: "Komplettera lektionen genom att generera bilder för varje ledtråd, eller ladda upp manuellt",
              color: "#ff6b6b",
            },

            {
              icon: "PersonOutline",
              text: "Klicka på Elevläge för att se hur lektionen ser ut för eleverna",

              color: "#ff6b6b",
            },
            {
              icon: "Share",
              text: "Dela lektionen via en länk som elever (inget elevkonto krävs) kan gå in på  eller via Studera.AI (elevkonto krävs)",

              color: "#ff6b6b",
            },
            {
              icon: "SaveAs",
              text: "Glöm inte att spara lektionen i din lektionsbank så du när som helst kan gå tillbaka och redigera",
              color: "#ff9e7d",
            },
            {
              icon: "Print",
              text: "Du kan även skriva ut lektionen om du inte vill att den ska vara interaktiv",
              color: "#ff6b6b",
            },
          ]}
        />
      </Box>
    </Box>
  );
};

export default ClueGenerator;
